// File: src/types/enums.ts
// User Management Enums
export enum UserRoleEnum {
  Admin = "admin",
  Coach = "coach",
  Client = "client",
}

export enum UserStatusEnum {
  Active = "active",
  PendingVerification = "pending_verification",
  Suspended = "suspended",
  Deactivated = "deactivated",
}

export enum PlanStatusEnum {
  Active = "active",
  Deprecated = "deprecated",
  ComingSoon = "coming_soon",
}

export enum BillingIntervalEnum {
  Monthly = "monthly",
  Yearly = "yearly",
}

export enum SubscriptionStatusEnum {
  Active = "active",
  Trial = "trial",
  PastDue = "past_due",
  Cancelled = "cancelled",
  Expired = "expired",
  PendingPayment = "pending_payment",
  PendingCancellation = "pending_cancellation", // For Stripe cancel_at_period_end
}

export enum InvitationStatusEnum {
  Pending = "pending",
  Accepted = "accepted",
  Expired = "expired",
  Revoked = "revoked",
}

// Daily Planner Enums
export enum ItemPriorityEnum {
  Low = "low",
  Normal = "normal",
  High = "high",
}

export enum ItemStatusEnum {
  Pending = "pending",       // Re-added for tasks, etc. to explicitly be pending
  InProgress = "in_progress",
  Completed = "completed",
  Cancelled = "cancelled",   // Re-added
  Missed = "missed",         // Re-added
  OnHold = "on_hold",        // Re-added
}

export enum GoalStatusEnum {
  Active = "active",
  Achieved = "achieved",
  Archived = "archived",
  OnHold = "on_hold",
}

export enum HabitFrequencyTypeEnum {
  Daily = "daily",
  XTimesPerWeek = "x_times_per_week",
  SpecificDays = "specific_days",
}

export enum HabitStatusEnum { // Status of the habit definition itself
  Active = "active",
  Paused = "paused",
  Achieved = "achieved", // Habit goal met
  Archived = "archived", // Habit no longer active
}

export enum HabitTrackingStatusEnum { // Status for a specific day's tracking
  Completed = "completed",
  PartiallyCompleted = "partially_completed",
  Missed = "missed",
  Skipped = "skipped", // Intentionally skipped
  Pending = "pending",
}

// NEW: Value Extraction Enum
export enum ValueExtractionStatusEnum {
    Locked = "locked",
    Unlocked = "unlocked",
    Submitted = "submitted",
    Assessed = "assessed",
    Prioritized = "prioritized", 
}

// Communication Enums
export enum MessageContentTypeEnum {
  Text = "text",
  VoiceMemo = "voice_memo",
  FileAttachment = "file_attachment",
  System = "system",
}

export enum AppointmentStatusEnum {
  Scheduled = "scheduled",             // Client or Coach proposed, pending confirmation
  Confirmed = "confirmed",             // Both parties agreed
  Completed = "completed",             // Session took place
  CancelledByCoach = "cancelled_by_coach",
  CancelledByClient = "cancelled_by_client",
  Rescheduled = "rescheduled",
  NoShowClient = "no_show_client",
  NoShowCoach = "no_show_coach",
  PendingRequest = "pending_request",  // NEW: Client proposed, awaiting coach approval
  RejectedByCoach = "rejected_by_coach", // NEW: Coach rejected client's proposed slot
  ProposedByCoach = "proposed_by_coach", // NEW: Coach proposed a slot, awaiting client's confirmation
  RejectedByClient = "rejected_by_client", // NEW: Client rejected coach's proposed slot
}

export enum NotificationTypeEnum {
  NewMessage = "new_message",
  AppointmentReminder = "appointment_reminder",
  AppointmentScheduled = "appointment_scheduled",
  AppointmentUpdated = "appointment_updated",
  AppointmentCancelled = "appointment_cancelled",
  TaskDueReminder = "task_due_reminder",
  TaskCreated = "task_created",
  TaskCompletedByClient = "task_completed_by_client",
  HabitReminder = "habit_reminder",
  GoalMilestoneAchieved = "goal_milestone_achieved",
  GoalCreated = "goal_created",
  NewClientAssigned = "new_client_assigned",
  InvitationAccepted = "invitation_accepted",
  SubscriptionUpdate = "subscription_update",
  PaymentSuccess = "payment_successful",
  PaymentFailed = "payment_failed",
  WeeklyProgressSummary = "weekly_progress_summary",
  NewTemplateShared = "new_template_shared",
  AppointmentRequestReceived = "appointment_request_received",
  AppointmentRequestApproved = "appointment_request_approved",
  AppointmentRequestRejected = "appointment_request_rejected",
  AppointmentProposalReceived = "appointment_proposal_received",
  AppointmentProposalAccepted = "appointment_proposal_accepted",
  AppointmentProposalRejected = "appointment_proposal_rejected",
  // --- ONBOARDING NOTIFICATION TYPES ---
  IntakeFormAssigned = "intake_form_assigned",
  IntakeFormSubmitted = "intake_form_submitted",
  IntakeFormReviewed = "intake_form_reviewed",
  // --- INVOICING NOTIFICATION TYPE ---
  InvoiceStatusUpdated = "invoice_status_updated",
  AppointmentStatusUpdated = "appointment_status_changed", // Corrected typo
  // --- NEW: GROUP RESOURCE SHARING ---
  ResourceSharedWithGroup = "resource_shared_with_group",
}

// Payment & System Enums
export enum PaymentTransactionStatusEnum {
  Pending = "pending",
  Succeeded = "succeeded",
  Failed = "failed",
  Refunded = "refunded",
  PartiallyRefunded = "partially_refunded",
  Chargeback = "chargeback",
}

export enum CalendarProviderEnum {
  Google = "google",
  Outlook = "outlook",
  Apple = "apple",
  Other = "other",
}

export enum ActivityTypeEnum {
  GOAL_CREATED = "goal_created",
  GOAL_UPDATED = "goal_updated",
  OBJECTIVE_CREATED = "objective_created",
  OBJECTIVE_UPDATED = "objective_updated",
  TODO_CREATED = "todo_created",
  TODO_UPDATED = "todo_updated",
  HABIT_CREATED = "habit_created",
  HABIT_UPDATED = "habit_updated",
  HABIT_TRACKED = "habit_tracked",
  JOURNAL_ENTRY_CREATED = "journal_entry_created",
  JOURNAL_ENTRY_UPDATED = "journal_entry_updated",
  AGENDA_ITEM_CREATED = "agenda_item_created",
  AGENDA_ITEM_UPDATED = "agenda_item_updated",
  WELL_BEING_LOGGED = "well_being_logged",
  WEEKLY_SUMMARY_SUBMITTED = "weekly_summary_submitted",
  WOL_ASSESSMENT_SUBMITTED = "wol_assessment_submitted",
  VALUES_SUBMISSION_SUBMITTED = "values_submission_submitted",
  VALUES_PRIORITIZED = "values_prioritized",
}

export enum SummaryStatusEnum {
  Pending = "pending",
  Processing = "processing",
  Completed = "completed",
  Failed = "failed",
}

// --- ONBOARDING ENUMS ---
export enum IntakeFormStatusEnum {
    Pending = "pending",
    Submitted = "submitted",
    Reviewed = "reviewed",
}

export enum IntakeQuestionTypeEnum {
    TextShort = "text_short",
    TextLong = "text_long",
    Dropdown = "dropdown",
    Multiselect = "multiselect",
    Checkbox = "checkbox",
    RatingScale = "rating_scale",
    YesNo = "yes_no",
    Email = "email",
    Phone = "phone",
}

// --- INVOICING ENUMS ---
export enum InvoiceStatusEnum {
    Draft = "draft",
    Sent = "sent",
    Paid = "paid",
    Overdue = "overdue",
    Cancelled = "cancelled",
}